import { CostAllocationMain } from 'components/CostAllocation';
import styles from './index.module.scss';
import { AppDispatch } from 'redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { updateLayOut } from 'redux/slice/layoutExpandSlice';
import { LayoutExpandInterFace } from './types';
import { MaximizeIcon, MinimizeIcon } from 'utils/svgs';
import { useEffect, useState } from 'react';
import { checkPermission, getLastDateOfMonth } from 'utils/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PreviewJournal from 'components/JournalEntries/PreviewJournal';
import { payPeriodOptionsAction } from 'redux/action/payPeriodAction';
import { Space, Tabs } from 'antd';
import JournalEntriesMain from 'components/JournalEntries/JournalEntriesMain';
import { Dayjs } from 'dayjs';
import ExportData from 'components/Global/ExportData';
import { EyeFilled } from '@ant-design/icons';

export default function CostAllocationPage() {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const [queryParams] = useSearchParams();

	const { isExpanded } = useSelector(
		(state: LayoutExpandInterFace) => state?.layoutExpand
	);

	const [isPreviewJournalOpen, setIsPreviewJournalOpen] = useState(false);
	const [payPeriodId, setPayPeriodId] = useState<string | null>(null);
	const [payPeriodLastDate, setPayPeriodLastDate] = useState<
		string | Date | null | Dayjs
	>(null);
	const [payPeriodData, setPayPeriodData] = useState(null);
	const [isCostAllocation, setIsCostAllocation] = useState('1');

	const [searchValue, setSearchValue] = useState('');
	const [filterValue, setFilterValue] = useState<any>({});
	const [sortBy, setSortBy] = useState<string | null>('fullName');
	const [sortType, setSortType] = useState<string | null>('asc');

	const onScreenChange = () => {
		const goingFull = !document.fullscreenElement; // what will happen next
		dispatch(updateLayOut({ isExpanded: goingFull }));  // update instantly
		if (goingFull) {
			document.documentElement.requestFullscreen();
		} else {
			document.exitFullscreen();
		}
	};

	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	const costAllocationData = useSelector(
		(state: any) => state?.costAllocation?.costAllocationData
	);

	const payPeriods = useSelector((state: any) => state?.payPeriods?.optionData);

	const isViewCostAllocationPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Cost Allocations',
			permission: ['view'],
		}
	);

	const isJournalEntriesPermission = checkPermission(
		selectedCompanyPermission,
		{
			permissionName: 'Journals Entries',
			permission: ['view'],
		}
	);

	useEffect(() => {
		if (!isViewCostAllocationPermission && isJournalEntriesPermission) {
			setIsCostAllocation('2');
		}
		if (
			!isViewCostAllocationPermission &&
			!isJournalEntriesPermission &&
			selectedCompanyPermission
		) {
			navigate('/unauthorized');
		}
	}, [
		isViewCostAllocationPermission,
		isJournalEntriesPermission,
		selectedCompanyPermission,
	]);

	// Re-applies an explicit `tab` query param on every navigation here, not
	// just the first mount (isCostAllocation above is a plain useState with
	// no query-param read at all) — otherwise the AI assistant (or any link)
	// sending the user back to "Cost Allocations" does nothing when the user
	// is already on this page and previously clicked into the internal
	// Journal Entries tab, since React Router doesn't remount a routed page
	// for a same-pathname navigation. Gated on both tabs being visible,
	// matching the permission effect above — with only one tab available,
	// that one already owns the tab state.
	useEffect(() => {
		if (!(isViewCostAllocationPermission && isJournalEntriesPermission)) {
			return;
		}
		const tab = queryParams.get('tab');
		if (tab === 'journalEntries') {
			setIsCostAllocation('2');
		} else if (tab === 'costAllocation') {
			setIsCostAllocation('1');
		}
	}, [queryParams.get('tab'), isViewCostAllocationPermission, isJournalEntriesPermission]);

	useEffect(() => {
		document.title = 'CostAllocation Pro - Cost Allocations';
		dispatch(payPeriodOptionsAction());

		return () => {
			dispatch(updateLayOut({ isExpanded: false }));
		};
	}, []);


	useEffect(() => {
		const handleFullscreenChange = () => {
			// If fullscreen mode ends (ESC, browser UI, system keys)
			if (!document.fullscreenElement) {
				dispatch(updateLayOut({ isExpanded: false }));
			}
		};

		document.addEventListener("fullscreenchange", handleFullscreenChange);
		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
		};
	}, []);

	useEffect(() => {
		if (payPeriodId) {
			const selectedPayPeriod = payPeriods.find(
				(singlePayPeriod: any) => singlePayPeriod.id === payPeriodId
			);
			setPayPeriodData(selectedPayPeriod);
			const lastDate = getLastDateOfMonth(selectedPayPeriod?.endDate);
			setPayPeriodLastDate(lastDate);
		}
	}, [payPeriods, payPeriodId]);

	const handleOpenPreviewJournal = () => {
		setIsPreviewJournalOpen(true);
	};

	const handleClosePreviewJournal = () => {
		setIsPreviewJournalOpen(false);
		setIsCostAllocation('2');
	};

	const handlePayPeriodId = (payPeriodId: string) => {
		setPayPeriodId(payPeriodId);
	};

	let items;

	if (isViewCostAllocationPermission && isJournalEntriesPermission) {
		items = [
			{
				key: '1',
				label: 'Cost Allocation',
				children: (
					<>
						<div className={styles['table-cost-allocation__tabs-header']}>
							{costAllocationData &&
								costAllocationData.length > 0 &&
								isJournalEntriesPermission && (
									<div
										className="table-cost-allocation__tabs-header--preview-journal"
										data-tour-target="tutorial-preview-journal"
									>
										<button
											className={
												styles[
													'table-cost-allocation__tabs-header--preview-journal--btn'
												]
											}
											onClick={handleOpenPreviewJournal}
										>
											Preview Journal &ensp;
											<EyeFilled />
										</button>
									</div>
								)}
							{costAllocationData && costAllocationData.length > 0 && (
								<div className="table-cost-allocation__tabs-header--export">
									<Space className="search-filter-export">
										<ExportData
											moduleName="costAllocations"
											params={{
												payPeriodId: payPeriodId,
												filterValue,
												searchValue,
												sort: sortBy,
												type: sortType,
											}}
										/>
									</Space>
								</div>
							)}
							{isCostAllocation === '1' && (
								<button
									onClick={() => onScreenChange?.()}
									className={styles['actions-layoutExpand']}
								>
									{isExpanded ? (
										<>
											<MinimizeIcon /> Minimize
										</>
									) : (
										<>
											<MaximizeIcon /> Maximize
										</>
									)}
								</button>
							)}
						</div>
						<div className={styles['table-cost-allocation']}>
							<CostAllocationMain
								handlePayPeriodId={handlePayPeriodId}
								searchValue={searchValue}
								setSearchValue={setSearchValue}
								filterValue={filterValue}
								setFilterValue={setFilterValue}
								sortBy={sortBy}
								setSortBy={setSortBy}
								sortType={sortType}
								setSortType={setSortType}
								isCostAllocation={isCostAllocation}
								onScreenChange={onScreenChange}
								isExpanded={isExpanded}
							/>
						</div>
					</>
				),
			},
			{
				key: '2',
				label: 'Journal Entries',
				children: (
					<>
					<div className={styles['table-cost-allocation__tabs-header']}>
						{isCostAllocation === '2' && (
							<button
								onClick={() => onScreenChange?.()}
								className={styles['actions-layoutExpand']}
							>
								{isExpanded && (
									<>
									<MinimizeIcon /> Minimize
									</>
								)}
							</button>
						)}
						</div>
						<JournalEntriesMain />
					</>
				),
			},
		];
	} else if (isViewCostAllocationPermission && !isJournalEntriesPermission) {
		items = [
			{
				key: '1',
				label: 'Cost Allocation',
				children: (
					<>
						<div className={styles['table-cost-allocation__tabs-header']}>
							{costAllocationData &&
								costAllocationData.length > 0 &&
								isJournalEntriesPermission && (
									<div
										className="table-cost-allocation__tabs-header--preview-journal"
										data-tour-target="tutorial-preview-journal"
									>
										<button
											className={
												styles[
													'table-cost-allocation__tabs-header--preview-journal--btn'
												]
											}
											onClick={handleOpenPreviewJournal}
										>
											Preview Journal &ensp;
											<EyeFilled />
										</button>
									</div>
								)}
							{costAllocationData && costAllocationData.length > 0 && (
								<div className="table-cost-allocation__tabs-header--export">
									<Space className="search-filter-export">
										<ExportData
											moduleName="costAllocations"
											params={{
												payPeriodId: payPeriodId,
												filterValue,
												searchValue,
												sort: sortBy,
												type: sortType,
											}}
										/>
									</Space>
								</div>
							)}
						</div>
						<div className={styles['table-cost-allocation']}>
							<CostAllocationMain
								handlePayPeriodId={handlePayPeriodId}
								searchValue={searchValue}
								setSearchValue={setSearchValue}
								filterValue={filterValue}
								setFilterValue={setFilterValue}
								sortBy={sortBy}
								setSortBy={setSortBy}
								sortType={sortType}
								setSortType={setSortType}
								isCostAllocation={isCostAllocation}
								onScreenChange={onScreenChange}
								isExpanded={isExpanded}
							/>
						</div>
					</>
				),
			},
		];
	} else if (!isViewCostAllocationPermission && isJournalEntriesPermission) {
		items = [
			{
				key: '2',
				label: 'Journal Entries',
				children: <JournalEntriesMain />,
			},
		];
	}

	const changeCostAllocationTab = (isCostAllocation: boolean) => {
		if (isCostAllocation) {
			setIsCostAllocation('1');
		} else {
			setIsCostAllocation('2');
		}
	};

	return (
		<>
			<div className={styles['cost-allocation-container']}>
				{/* <div className={styles['cost-allocation-container__header']}>
					<div className={styles['cost-allocation-container__header--title']}>
						Cost Allocations
					</div>
					<div className={styles['cost-allocation-container__header--actions']}>
						{isCostAllocation == '1' && (
							<button
								onClick={() => onScreenChange()}
								className={
									styles[
										'cost-allocation-container__header--actions-layoutExpand'
									]
								}
							>
								{isExpanded ? (
									<>
										<MinimizeIcon /> Minimize
									</>
								) : (
									<>
										<MaximizeIcon /> Maximize
									</>
								)}
							</button>
						)}
						{costAllocationData &&
							costAllocationData.length > 0 &&
							isJournalEntriesPermission && (
								<button
									className={
										styles['cost-allocation-container__header--actions-preview']
									}
									onClick={handleOpenPreviewJournal}
								>
									Preview Journal
								</button>
							)}
					</div>
				</div> */}
				<div className={styles['cost-allocation-container__table']}>
					<div className={styles['cost-allocation-main']}>
						<Tabs
							defaultActiveKey="2"
							items={items}
							activeKey={isCostAllocation}
							key={isCostAllocation}
							tabBarStyle={{
								paddingLeft: '20px',
								fontSize: '18px',
							}}
							onChange={(e) => {
								changeCostAllocationTab(e === '1');
							}}
						/>
					</div>
				</div>
			</div>
			{isPreviewJournalOpen && (
				<PreviewJournal
					handleClosePreviewJournal={handleClosePreviewJournal}
					isPreviewJournalOpen={isPreviewJournalOpen}
					payPeriodLastDate={payPeriodLastDate}
					payPeriodId={payPeriodId}
					payPeriodData={payPeriodData}
				/>
			)}
		</>
	);
}
